import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { createSearchClient, SearchClient } from "../../scripts/rag-service/src/spec/create-client"
import type { AnySFFRagDocumentResult, SearchIntentParams, SFFRagDocumentTypeName } from "../../server/search/rag"

let searchClient!: SearchClient

export function useSearchClient() {
  const client = useMemo<SearchClient>(() => {
    if (env.client && !searchClient) {
      searchClient = createSearchClient(env.dev && false ? "http://pi-ai:9988" : "https://sff-search.daniellever.com")
      // searchClient = createSearchClient("https://sff-search.daniellever.com")
    }
    return searchClient
  }, [])

  return client
}

type SearchParams = {
  query: string
  enabled?: boolean
}

export type VectorKeywordResult = {
  score: number
  type: string
  text: string
  label: string
}

export type VectorSearchResults = {
  documents: AnySFFRagDocumentResult[]
  keywords: VectorKeywordResult[]
}

export function useVectorSearch(params: SearchParams) {
  const client = useSearchClient()
  const search = useQuery({
    queryKey: ["query", "quick-search", params],
    queryFn: async (args) => {
      const params = args.queryKey[2] as SearchParams
      const result = (await client.quickSearch(
        {
          q: params.query,
        },
        {
          signal: args.signal,
        },
      )) as any as VectorSearchResults
      return result
    },
    enabled: params.query.length > 0 && params.enabled !== false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    placeholderData: (prev) => prev,
    staleTime: 3600 * 1000,
  })

  return search
}

export function useSearchIntent(params: SearchParams) {
  const client = useSearchClient()
  const search = useQuery({
    queryKey: ["query", "search-intent", params],
    queryFn: (args) => {
      const params = args.queryKey[2] as SearchParams
      return client.searchType(
        {
          q: params.query,
          preset: "searchIntent",
        },
        {
          signal: args.signal,
        },
      ) as any as SearchIntentParams
    },
    enabled: params.query.length > 0 && params.enabled !== false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    placeholderData: (prev) => prev,
    staleTime: 3600 * 1000,
  })

  return search
}

type RelationParams = {
  id: number
  documentTypes?: SFFRagDocumentTypeName[]
  chunkTypes?: string[]
}

export function useRelatedItems(params: RelationParams) {
  const client = useSearchClient()
  const search = useQuery({
    queryKey: ["query", "related-films", params],
    queryFn: (args) => {
      const params = args.queryKey[2] as RelationParams
      return client.relevantDocuments(
        {
          id: params.id,
          documentTypes: params.documentTypes,
          chunkTypes: params.chunkTypes,
        },
        {
          signal: args.signal,
        },
      )
    },
    refetchOnWindowFocus: false,
    refetchInterval: false,
    placeholderData: (prev) => prev,
    staleTime: 3600 * 1000,
  })

  return search
}
